import { styled } from '@mui/material/styles';
import { resetNavbar, selectFuseNavbar } from '@/components/theme-layouts/components/navbar/navbarSlice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import { Theme } from '@mui/system';
import { useEffect } from 'react';
import useFuseLayoutSettings from '@fuse/core/FuseLayout/useFuseLayoutSettings';
import NavbarStyle2Content from './NavbarStyle2Content';
import { Layout1ConfigDefaultsType } from '@/components/theme-layouts/layout1/Layout1Config';

const navbarWidth = 280;

type StyledNavBarPropsProps = {
	theme?: Theme;
	folded: number;
	open: boolean;
	foldedandopened: number;
};

const Root = styled('div')<StyledNavBarPropsProps>(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	zIndex: 4,
	transition: theme.transitions.create(['width', 'min-width'], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.shorter,
	}),
	[theme.breakpoints.up('lg')]: {
		width: navbarWidth,
		minWidth: navbarWidth,
	},
	variants: [
		{
			props: ({ folded }) => folded,
			style: {
				[theme.breakpoints.up('lg')]: {
					width: 76,
					minWidth: 76,
				},
			},
		},
		{
			props: ({ foldedandopened }) => foldedandopened,
			style: {
				[theme.breakpoints.up('lg')]: {
					width: navbarWidth,
					minWidth: navbarWidth,
				},
			},
		},
	],
}));

type StyledNavBarProps = {
	theme?: Theme;
	open?: boolean;
	folded: number;
	foldedandopened: number;
	foldedandclosed: number;
	position?: string;
	anchor?: string;
};

const StyledNavbar = styled('div')<StyledNavBarProps>(({ theme }) => ({
	minWidth: navbarWidth,
	width: navbarWidth,
	maxWidth: navbarWidth,
	maxHeight: '100%',
	transition: theme.transitions.create(['width', 'min-width'], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.shorter,
	}),
	'& .NavbarStyle2-content .fuse-list-item': {
		transition: theme.transitions.create(
			['width', 'min-width', 'padding', 'margin', 'border-radius', 'background-color', 'color'],
			{
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.shorter,
			},
		),
	},
	variants: [
		{
			props: {
				position: 'left',
			},
			style: {
				borderRight: '1px solid var(--neutral-outline)',
				left: 0,
			},
		},
		{
			props: {
				position: 'right',
			},
			style: {
				borderLight: '1px solid var(--neutral-outline)',
				right: 0,
			},
		},
		{
			props: ({ folded }) => folded,
			style: {
				width: 76,
				minWidth: 76,
			},
		},
		{
			props: ({ foldedandopened }) => foldedandopened,
			style: {
				width: navbarWidth,
				minWidth: navbarWidth,
			},
		},
		{
			props: ({ foldedandclosed }) => foldedandclosed,
			style: {
				'& .NavbarStyle2-content': {
					'& .logo-icon': {
						display: 'none',
					},
					'& .logo-text': {
						display: 'none',
					},
					'& .react-badge': {
						opacity: 0,
					},
					'& .fuse-list-item': {
						width: 52,
						height: 50,
						minWidth: 52,
						// Padding instead of justify-content: center, so the icon position animates.
						justifyContent: 'flex-start !important',
						paddingLeft: '18px !important',
						paddingRight: '0 !important',
						paddingTop: '0 !important',
						paddingBottom: '0 !important',
						marginLeft: '12px !important',
						marginRight: '0 !important',
						borderRadius: '8px !important',
						display: 'flex !important',
						alignItems: 'center !important',
					},
					'& .fuse-list-item.active, & .fuse-list-item.has-active-child': {
						backgroundColor: 'var(--neutral-bg-active) !important',
						'& .fuse-list-item-icon': {
							color: 'var(--primary-500) !important',
						},
					},
					'& .fuse-list-item-icon': {
						marginRight: '0 !important',
						marginLeft: '0 !important',
						marginTop: '0 !important',
						marginBottom: '0 !important',
						margin: '0 !important',
					},
					'& .fuse-list-item-text, & .item-badge': {
						display: 'none',
					},
					'& .arrow-icon': {
						display: 'none',
					},
					'& .MuiIconButton-root:has(.arrow-icon)': {
						display: 'none',
					},
					'& .fuse-list-subheader .fuse-list-subheader-text': {
						opacity: 0,
					},
					'& .fuse-list-subheader:before': {
						content: '""',
						display: 'block',
						position: 'absolute',
						minWidth: 16,
						borderTop: '2px solid',
						opacity: 0.2,
					},
					'& .collapse-children': {
						display: 'none',
					},
					'& .user-menu': {
						minWidth: 52,
						'& .title': {
							opacity: 0,
						},
						'& .subtitle': {
							opacity: 0,
						},
						'& .info-icon': {
							opacity: 0,
						},
						'& .arrow': {
							opacity: 0,
						},
					},
				},
			},
		},
	],
}));

/**
 * The navbar style 2.
 */
function NavbarStyle2() {
	const dispatch = useAppDispatch();

	const settings = useFuseLayoutSettings();
	const config = settings.config as Layout1ConfigDefaultsType;

	const navbar = useAppSelector(selectFuseNavbar);

	const folded = config.navbar?.folded;
	const foldedandclosed = folded && !navbar.foldedOpen;
	const foldedandopened = folded && navbar.foldedOpen;

	useEffect(() => {
		return () => {
			dispatch(resetNavbar());
		};
	}, [dispatch]);

	return (
		<Root
			folded={folded ? 1 : 0}
			foldedandopened={foldedandopened ? 1 : 0}
			open={navbar.open}
			id="fuse-navbar"
			className="sticky top-0 z-20 h-screen shrink-0"
		>
			<StyledNavbar
				className="sticky top-0 z-20 h-screen flex-auto shrink-0 flex-col overflow-hidden shadow-sm"
				position={config?.navbar?.position}
				folded={folded ? 1 : 0}
				foldedandopened={foldedandopened ? 1 : 0}
				foldedandclosed={foldedandclosed ? 1 : 0}
			>
				<NavbarStyle2Content className="NavbarStyle2-content" />
			</StyledNavbar>
		</Root>
	);
}

export default NavbarStyle2;
